import t from 'react-translate';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { config } from '@config/pendo.config.json';
import NvSelect, { SelectOption } from 'shared/components/inputs/nv-select';
import NumberInput from 'athena/components/number-input';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { CustomCommunicationMessageType } from 'redux/schemas/models/mentorship-program-custom-communications';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import messageTypeMap, { DEFAULT_SESSION_NUDGE_WEEKS } from '../../constants';

const FieldLabel = ({ text }: { text: string }) => (
  <label className='color-gray-2 semi-bold font-size-medium mb-1'>
    {text}
    <span className='color-danger'>*</span>
  </label>
);

const DeliverySection = () => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId, matchingType } = mentoringProgram || {};
  const mentoringProgramAliases = useSelector((state: any) => getMentoringProgramAliases(state, programId as number));
  const translationBase = t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL.EDIT_MODE;
  const dataQATag = config.pendo.athena.mentorshipProgram.communications.editCommunicationModal.delivery;
  const { watch, setValue, control } = useFormContext();
  const [toValue, messageTypeValue, weeksValue] = watch([
    'to',
    'messageType',
    'sessionInactivityThresholdWeeks',
  ]) as [TargetAudience, CustomCommunicationMessageType, string | number | undefined];

  const messageTypeData = useMemo(
    () => messageTypeMap(
      t,
      matchingType,
      mentoringProgramAliases,
      weeksValue ? Number(weeksValue) : DEFAULT_SESSION_NUDGE_WEEKS,
    ),
    [matchingType, mentoringProgramAliases, weeksValue],
  );

  const selectedMessageType = messageTypeValue ? messageTypeData[messageTypeValue] : null;
  const { title: selectedMessageTypeTitle, sentOn: selectedMessageTypeSentOn } = selectedMessageType || {};

  const isSessionNudge = messageTypeValue === CustomCommunicationMessageType.SESSION_NUDGE;
  const targetAudienceOptions: SelectOption[] = useMemo(
    () => [
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTORS_AND_MENTEES(
          mentoringProgramAliases,
        ),
        value: TargetAudience.MENTOR_AND_MENTEE,
        dataQa: dataQATag.audience.mentorsMenteesOption,
      },
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTORS(
          mentoringProgramAliases,
        ),
        value: TargetAudience.MENTOR,
        dataQa: dataQATag.audience.mentorsOption,
      },
      {
        label: translationBase.DELIVERY_SECTION.TO.OPTIONS.MENTEES(
          mentoringProgramAliases,
        ),
        value: TargetAudience.MENTEE,
        dataQa: dataQATag.audience.menteesOption,
      },
    ],
    [
      dataQATag.audience.menteesOption,
      dataQATag.audience.mentorsMenteesOption,
      dataQATag.audience.mentorsOption,
      mentoringProgramAliases,
      translationBase.DELIVERY_SECTION.TO.OPTIONS,
    ],
  );

  const messageTypeOptions = useMemo(
    () => Object.entries(messageTypeData).map(
      ([value, { title, sentOn, dataQa }]) => ({
        label: (
          <div className='d-flex align-items-center'>
            <span className='black'>{title}</span>
            <NvTooltip text={sentOn}>
              <NvIcon
                icon='info'
                className='message-type-tooltip ml-2'
                size='xss-small'
                tabIndex={0}
                altLabel={sentOn}
              />
            </NvTooltip>
          </div>
        ),
        value,
        dataQa,
      }),
    ),
    [messageTypeData],
  );

  return (
    <div className='edit-communication-delivery p-4 w-100'>
      <div className='d-flex flex-column'>
        <FieldLabel text={translationBase.DELIVERY_SECTION.TO.TITLE()} />
        <NvSelect
          value={toValue}
          className='bg-white align-items-center position-relative mb-2'
          options={targetAudienceOptions}
          onChange={(value) => setValue('to', value, { shouldValidate: true, shouldDirty: true })}
          placeholder={translationBase.PLACEHOLDERS.TO()}
          ariaLabel={translationBase.DELIVERY_SECTION.TO.TITLE()}
          required
        />
        <FieldLabel
          text={translationBase.DELIVERY_SECTION.MESSAGE_TYPE.TITLE()}
        />
        <NvSelect
          value={selectedMessageTypeTitle}
          className='bg-white align-items-center position-relative'
          renderFallbackValue
          options={messageTypeOptions}
          onChange={(value) => {
            setValue('messageType', value, {
              shouldValidate: true,
              shouldDirty: true,
            });

            if (value === CustomCommunicationMessageType.SESSION_NUDGE) {
              setValue('sessionInactivityThresholdWeeks', Number(weeksValue) || DEFAULT_SESSION_NUDGE_WEEKS, {
                shouldValidate: true,
                shouldDirty: true,
              });
              return;
            }

            setValue('sessionInactivityThresholdWeeks', null, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          placeholder={translationBase.PLACEHOLDERS.MESSAGE_TYPE()}
          ariaLabel={translationBase.DELIVERY_SECTION.MESSAGE_TYPE.TITLE()}
          required
        />
        {isSessionNudge && (
          <div className='mt-4'>
            <FieldLabel
              text={translationBase.DELIVERY_SECTION.SESSION_NUDGE_WEEKS.TITLE()}
            />
            <Controller
              name='sessionInactivityThresholdWeeks'
              control={control}
              render={({ field, fieldState: { error } }) => {
                const placeholder = '__SESSION_NUDGE_WEEKS_INPUT__';
                const weeksCount = weeksValue ? Number(weeksValue) : DEFAULT_SESSION_NUDGE_WEEKS;
                const getDescription = translationBase.DELIVERY_SECTION.SESSION_NUDGE_WEEKS
                  .DESCRIPTION as unknown as (input: string, weeksCount: number) => string;
                const descriptionText = String(
                  getDescription(placeholder, weeksCount),
                );
                const [beforeInput, afterInput = ''] = descriptionText.split(placeholder);

                return (
                  <div>
                    <div className='text-regular gray-1'>
                      <span>{beforeInput}</span>
                      <NumberInput
                        name={field.name}
                        data-qa={dataQATag.sessionNudgeWeeksInputField}
                        className={`session-nudge-weeks-input d-inline-flex ${error ? 'border border-danger' : ''}`}
                        min={1}
                        aria-label={translationBase.DELIVERY_SECTION.SESSION_NUDGE_WEEKS.ARIA_LABEL()}
                        aria-invalid={!!error}
                        value={weeksValue ? Number(weeksValue) : DEFAULT_SESSION_NUDGE_WEEKS}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                      <span>{afterInput}</span>
                    </div>
                    {error && (
                      <span className='text-danger text-small mt-1 d-block'>
                        {error.message}
                      </span>
                    )}
                  </div>
                );
              }}
            />
          </div>
        )}
        {!isSessionNudge && (
          <span className='mt-2 text-small gray-2'>
            {selectedMessageTypeSentOn}
          </span>
        )}
      </div>
    </div>
  );
};

export default DeliverySection;
